import { DecimalSchema } from "../decimal";
import { ArenaOnboardingStatusSchema } from "../shared/arena-onboarding-types";
import type { ArenaMatchOpponentDto } from "../shared/arena-opponent-types";
import { TokenBalanceTypesSchema } from "../shared/balance-types";
import {
  UneligibleTradeReason,
  SOCIAL_NETWORK,
  SocialName,
  SOCIAL_NETWORK_DISPLAY_NAMES,
} from "../shared/campaign-types";
import type { AirdropFarmingBotStatus } from "./airdrop-farming";
import type { ArenaGlobalChatMessageDto, ArenaGlobalChatModerationEvent } from "./cede-arena/arena-chat.dto";
import type { ArenaFeedEvent } from "./cede-arena/arena-feed.dto";
import type { ArenaOpeningMoveSide } from "./cede-arena/arena-opening-move.dto";
import { HubExchangeAccountSchema, HubUserSchema as HubUserSchemaBase } from "./hubUser";
import { QUEST_APP, QUEST_REWARD_TYPE, QUEST_TYPE } from "./quests/quest.enums";
import z from "zod";

export const TradeLeaderboardRecordSchema = z.object({
  id: z.number(),
  leaderboardId: z.number(),
  hubUserId: z.number(),
  rank: z.number(),
  volumeUSD: DecimalSchema,
  rewardTokenAmount: DecimalSchema,
  rewardTokenSymbol: z.string(),
  rewardClaimed: z.coerce.date().nullable(),
  hubUser: HubUserSchemaBase.optional(),
  tradesCount: z.number(),
});
export type TradeLeaderboardRecord = z.infer<typeof TradeLeaderboardRecordSchema>;

export const TradeLeaderboardRecordWithComputedEventsSchema = TradeLeaderboardRecordSchema.extend({
  computedEvents: z.array(z.number()).optional(),
});
export type TradeLeaderboardRecordWithComputedEvents = z.infer<typeof TradeLeaderboardRecordWithComputedEventsSchema>;

export const ValidateHubDefiAddressResponseSchema = z.object({
  isValid: z.boolean(),
  defiAddresses: z.array(z.string()).optional(),
  reason: z.nativeEnum(UneligibleTradeReason).optional(),
});
export type ValidateHubDefiAddressResponse = z.infer<typeof ValidateHubDefiAddressResponseSchema>;

export const NftBalanceSchema = z.object({
  metadataUri: z.string(),
});
export type NftBalance = z.infer<typeof NftBalanceSchema>;

/**
 * `/me` ships the social row as persisted, so the status is the database enum
 * (uppercase), not the lowercase `SOCIAL_STATUS` the socials endpoints map to.
 */
export const HUB_USER_SOCIAL_STATUS = {
  ONLINE: "ONLINE",
  OFFLINE: "OFFLINE",
} as const;

export const HubUserSocialSchema = z.object({
  id: z.number(),
  hubUserId: z.number(),
  socialName: z.union([z.nativeEnum(SOCIAL_NETWORK), z.string()]),
  userId: z.string().nullable().optional(),
  username: z.string().nullable().optional(),
  avatar: z.string().nullable().optional(),
  /** OFFLINE once a token is revoked or refresh fails; the row stays so the client can offer a reconnect. */
  status: z.nativeEnum(HUB_USER_SOCIAL_STATUS).optional(),
});
export type HubUserSocial = z.infer<typeof HubUserSocialSchema>;

export const HubUserMeResponseSchema = z.object({
  id: z.number(),
  // Null for users who signed up with email OTP and haven't linked a wallet yet
  defiAddress: z.string().nullable(),
  email: z.string().nullable().optional(),
  createdAt: z.coerce.date(),
  exchangeAccounts: HubExchangeAccountSchema.array(),
  socialAccounts: HubUserSocialSchema.array(),
  isBanned: z.boolean(),
  isAmbassador: z.boolean().nullable().optional(),
  ambassadorStatusUpdatedAt: z.coerce.date().nullable().optional(),
  isWhiteListed: z.boolean().nullable().optional(),
  nickname: z.string().max(32).nullable().optional(),
  preferredDisplaySocial: z.string().nullable().optional(),
  socialVisibilityEnabled: z.boolean().optional(),
  bestSocial: HubUserSocialSchema.nullable().optional(),
  nftBalances: NftBalanceSchema.array(),
  automaticStreakCguAcknowledged: z.boolean().nullable().optional(),
  // Optional so legacy serializers that predate arena onboarding stay valid.
  arenaOnboardingStatus: ArenaOnboardingStatusSchema.optional(),
  /**
   * `{ experimentKey: variant }` for every experiment this user is enrolled in.
   * A key being absent means "not enrolled" — it does NOT mean control, so read
   * sites must branch to their default behaviour explicitly.
   */
  experiments: z.record(z.string(), z.string()).optional(),
});
export type HubUserMeResponse = z.infer<typeof HubUserMeResponseSchema>;

export const HubUserSignInResponseSchema = z.object({
  user: HubUserMeResponseSchema.omit({ exchangeAccounts: true, socialAccounts: true, nftBalances: true }),
  isNewUser: z.boolean(),
});
export type HubUserSignInResponseDto = z.infer<typeof HubUserSignInResponseSchema>;

export const HubUserLinkWalletResponseSchema = z.object({
  defiAddress: z.string(),
});
export type HubUserLinkWalletResponseDto = z.infer<typeof HubUserLinkWalletResponseSchema>;

export const HubUserLinkEmailResponseSchema = z.object({
  email: z.string(),
});
export type HubUserLinkEmailResponseDto = z.infer<typeof HubUserLinkEmailResponseSchema>;

export const AutomaticStreakCguAcknowledgmentResponseSchema = z.object({
  success: z.boolean(),
});
export type AutomaticStreakCguAcknowledgmentResponseDto = z.infer<
  typeof AutomaticStreakCguAcknowledgmentResponseSchema
>;

// Re-export social network types from shared (moved to avoid circular dependency)
export { SOCIAL_NETWORK, SOCIAL_NETWORK_DISPLAY_NAMES };
export type { SocialName };

const BalanceByTokenSchema = z.record(
  z.string(), // tokenSymbol
  TokenBalanceTypesSchema,
);

export type BalanceByToken = z.infer<typeof BalanceByTokenSchema>;

const BalancesByWalletTypeSchema = z.record(
  z.string(), // walletType
  BalanceByTokenSchema,
);

export const HubExchangeAccountBalancesSchema = z.record(
  z.string(), // accountId (coerced from number)
  BalancesByWalletTypeSchema,
);
export type HubExchangeAccountBalances = z.infer<typeof HubExchangeAccountBalancesSchema>;

export const CheckCredentialsResponseSchema = z.object({
  valid: z.boolean(),
});
export type CheckCredentialsResponse = z.infer<typeof CheckCredentialsResponseSchema>;

export enum NotificationType {
  PING = "ping",
  WASH_TRADE = "wash_trade",
  SOCIAL_ACCOUNT_CONNECTED = "social_account_connected",
  MAINTENANCE = "maintenance",
  SPINNING_WHEEL_RESULT = "spinning_wheel_result",
  SOCIAL_ACCOUNT_DEACTIVATED = "social_account_deactivated",
  QUEST_COMPLETED = "quest_completed",
  AUTOMATIC_STREAK_PROGRESS = "automatic_streak_progress",
  FEATURE_FLAG_CHANGED = "feature_flag_changed",
  AIRDROP_BOT_UPDATED = "airdrop_bot_updated",
  EXTERNAL_ORDER_DETECTED = "external_order_detected",
  ARENA_BALANCE_UPDATED = "arena_balance_updated",
  ARENA_CAMPAIGN_OPT_IN_COUNT_CHANGED = "arena_campaign_opt_in_count_changed",
  ARENA_TOURNAMENT_UPDATED = "arena_tournament_updated",
  ARENA_ENTRY_UPDATED = "arena_entry_updated",
  ARENA_PRIVATE_ROOM_UPDATED = "arena_private_room_updated",
  ARENA_PRIVATE_COMPETITION_STARTED = "arena_private_competition_started",
  ARENA_COMPETITION_FINALIZED = "arena_competition_finalized",
  ARENA_TICKETS_GRANTED = "arena_tickets_granted",
  ARENA_PLAYER_STATS_UPDATED = "arena_player_stats_updated",
  ARENA_QUEUE_INVITE = "arena_queue_invite",
  ARENA_FRIEND_REQUEST_RECEIVED = "arena_friend_request_received",
  /** Arena-only broadcast (gated on `app=arena`). Never a CHAT item. */
  ARENA_FEED_EVENT = "arena_feed_event",
  ARENA_GLOBAL_CHAT_MESSAGE = "arena_global_chat_message",
  ARENA_GLOBAL_CHAT_MESSAGE_BANNED = "arena_global_chat_message_banned",
  ARENA_KOL_INVITE_ACCEPTED = "arena_kol_invite_accepted",
  ARENA_OPENING_MOVE_FILLED = "arena_opening_move_filled",
  ARENA_OPENING_MOVE_FAILED = "arena_opening_move_failed",
  PAPER_ORDER_CANCELLED = "paper_order_cancelled",
  PAPER_POSITION_UPDATED = "paper_position_updated",
  PAPER_ACCOUNT_CHANGED = "paper_account_changed",
  USER_STATS_UPDATED = "user_stats_updated",
  // API-liveness heartbeat + global-stats deltas ride the notifications SSE as
  // plain `message` events (dispatched by type), not dedicated named events.
  HEALTH = "health",
  GLOBAL_STATS_DELTA = "global.stats.delta",
  /** Transport-only liveness tick. Carries no payload and drives no UI beyond
   *  the client's connection watchdog — see PING, which is 20s and also
   *  refreshes arena presence. */
  TRANSPORT_PING = "transport_ping",
}

export interface WashTradeNotificationData {
  orderId: string;
  exchangeId: string;
  pairSymbol: string;
  campaignName?: string;
}

export interface SocialAccountConnectedNotificationData {
  socialName: string;
  username: string;
}

export interface MaintenanceNotificationData {
  isActive: boolean;
}
export interface SpinningWheelResultNotificationData {
  resultId: string;
  ticketId: string;
  rewardAmount: string;
  rewardSymbol: string;
  timestamp: number;
}

export interface SocialAccountDeactivatedNotificationData {
  socialName: string;
}

export interface QuestCompletedNotificationData {
  questId: number;
  correlationId: string;
  /**
   * The product surface the quest belongs to. Hub and Arena share this stream, so each
   * must react only to its own catalogue — without it, an Arena completion toasts in Hub.
   */
  app: QUEST_APP;
}

export interface AutomaticStreakProgressNotificationData {
  streakId: string;
  status: string;
  completedTrades: number;
  totalTrades: number;
  totalVolumeTraded: string;
  stopReason?: string;
  correlationId: string;
  pairSymbol: string;
  campaignName: string;
}

export interface FeatureFlagChangedNotificationData {
  key: string;
  oldValue: boolean;
  newValue: boolean;
}

export interface AirdropBotUpdatedNotificationData {
  botId: number;
  status: AirdropFarmingBotStatus;
  pair: string;
  totalVolumeUsdc: string;
  targetVolumeUsdc: string;
  totalPnlUsdc: string;
}

export interface UserStatsUpdatedNotificationData {
  campaignName: string;
  epochId: number;
}

export interface ExternalOrderDetectedNotificationData {
  orderId: string;
  campaignName: string;
  epochId: number;
  defiAddress: string;
}

export interface ArenaBalanceUpdatedNotificationData {
  freeUsdt: string;
  lockedUsdt: string;
  totalUsdt: string;
}

/** A tournament's boards, pool or rounds moved: refetch what is on screen, mark the rest stale. */
export interface ArenaTournamentUpdatedNotificationData {
  tournamentSlug: string;
}

export interface ArenaCampaignOptInCountChangedNotificationData {
  /** Canonical competition name (slug). */
  campaignName: string;
  /** Users currently opted in (status IN) to this competition after the change. */
  optedInCount: number;
  /** Who just joined, so the waiting stage names them without a refetch. */
  joiner?: { hubUserId: number; nickname: string | null; joinedAt: string };
  /** Who just left, so the waiting stage drops them without a refetch. */
  leaverHubUserId?: number;
}

/**
 * Pushed on every arena entry status transition so the matchmaking
 * UI reacts in real time instead of waiting for the poll. `event` names the
 * transition; the rest is the minimal match context the FE needs to render it
 * (the FE still refetches the entry — this payload is a wake-up, not the
 * source of truth).
 */
export interface ArenaEntryUpdatedNotificationData {
  entryId: number;
  entryType: string;
  status: string;
  /** `SEAT_FILLED`: the seat count of the entry's Spin & Go table changed (a seat filled or freed) — refetch the entry. */
  event: "MATCH_PROPOSED" | "READY_CONFIRMED" | "ASSIGNED" | "CANCELLED" | "REQUEUED" | "SEAT_FILLED";
  /** Same reveal rule as the REST payload. `null` = no opponent on this event, not "hidden". */
  opponent: ArenaMatchOpponentDto | null;
  campaignName: string | null;
  /** ISO timestamp of the duel's epoch start — set on ASSIGNED, null otherwise. */
  campaignStartsAt: string | null;
  /** ISO timestamp; set on MATCH_PROPOSED. */
  readyDeadlineAt: string | null;
  cancellationReason: string | null;
  /** ISO timestamp; set on REQUEUED (opponent no-show cooldown). */
  rematchCooldownUntil: string | null;
}

/**
 * Pushed to every member of a private competition room when the roster or
 * config changes (someone joined / left / was kicked, the owner edited the
 * config, or the room was cancelled). A wake-up — the FE refetches the room
 * by `slug`; this payload only names what happened.
 */
export interface ArenaPrivateRoomUpdatedNotificationData {
  /** Unique room slug (the invite-link segment). */
  slug: string;
  event: "JOINED" | "LEFT" | "KICKED" | "CONFIG_UPDATED" | "CANCELLED";
}

/**
 * Pushed to every member of a private competition room when the owner starts
 * it. Carries the name of the materialized competition campaign so each client
 * can redirect to the battle-arena page.
 */
export interface ArenaPrivateCompetitionStartedNotificationData {
  slug: string;
  campaignName: string;
}

export interface ArenaCompetitionFinalizedNotificationData {
  campaignName: string;
  epochId: number;
}

/**
 * Pushed the moment free-entry tickets actually mint, so the client refreshes
 * its inventory instead of waiting on the ticket poll.
 *
 * It is deliberately NOT derived from the competition finalizing: the grant runs
 * as its own job after finalize, so a client refetching on
 * `ARENA_COMPETITION_FINALIZED` can beat the rows into existence. This fires
 * because the tickets exist.
 */
export interface ArenaTicketsGrantedNotificationData {
  /**
   * Which grant minted them — the client's reveal surfaces are per-source.
   * Mirrors `KNOWN_ARENA_TICKET_SOURCES`; a source added on the API before this
   * bundle shipped simply won't match a branch, and the inventory still refetches.
   */
  source: "STARTER_PACK" | "ADMIN_GRANT" | "PARTNER_LINK" | "QUEST";
  ticketCount: number;
  totalValueUsdt: string;
}

/**
 * Pushed to one player when their materialized Arena battle record is rewritten,
 * so an open profile card shows the settled numbers without a reload.
 *
 * Like the ticket grant, it is deliberately NOT derived from the competition
 * finalizing: the rebuild runs as its own job after the finalize stamp, so a
 * client refetching on `ARENA_COMPETITION_FINALIZED` can read the row as it was
 * before the duel counted. This fires because the row is written, and only when
 * the numbers actually moved.
 */
export interface ArenaPlayerStatsUpdatedNotificationData {
  /** Whose record changed. The card is keyed by it, and it is the sole recipient. */
  hubUserId: number;
}

/**
 * Somebody is queued alone at a paid buy-in and past players of it are being
 * recruited. Delivered in-app to recipients who already have Arena open (their
 * browser-push twin goes to everyone else). The CTA opens the stake picker at
 * `amountUsdt`.
 */
export interface ArenaQueueInviteNotificationData {
  /**
   * Which ladder `amountUsdt` is a rung of — always one of the paid tiers
   * (`ArenaPaidTierDto`), typed here as a string to keep this module off the
   * cede-arena barrel. Consumers narrow it with `ArenaPaidTierSchema`; an
   * unrecognised value means DROP the invite, never assume the Standard ladder.
   */
  tier: string;
  /** The buy-in being advertised, in decimal USDT. */
  amountUsdt: string;
  /** RETURNING ⇒ the recipient has played this buy-in; NEW ⇒ they are being invited to try it. */
  variant: "RETURNING" | "NEW";
}

export type ArenaGlobalChatMessageBannedNotificationData = ArenaGlobalChatModerationEvent;

/** Pushed to the player when their armed opening move is placed at the bell. */
export interface ArenaOpeningMoveFilledNotificationData {
  campaignName: string;
  pairSymbol: string;
  side: ArenaOpeningMoveSide;
  fillPrice: string;
}

/** Pushed to the player when their armed opening move could not be placed at the bell. */
export interface ArenaOpeningMoveFailedNotificationData {
  campaignName: string;
  pairSymbol: string;
  side: ArenaOpeningMoveSide;
  failureReason: string;
}

/**
 * Pushed when a user's paper-trading order is cancelled by the async matching
 * engine — currently only a reduce-only market close that found no liquidity
 * (the one case a close silently leaves the position open). A wake-up + toast;
 * the positions/account queries are the source of truth.
 */
export interface PaperOrderCancelledNotificationData {
  /** Paper campaign the cancelled order belonged to. */
  campaignName: string;
  /** Base asset of the pair (e.g. "BTC"). */
  pairSymbol: string;
  /** FE-generated order id (`Paper-<uuid>`), for client-side dedupe/correlation. */
  externalOrderId: string;
  /** Why the engine cancelled it (e.g. "NO_LIQUIDITY", "INSUFFICIENT_MARGIN"). */
  cancelReason: string;
  /**
   * The cancelled order's reduce-only intent: true = a close/reduce attempt,
   * false = an open. It does NOT assert the resulting position state — a
   * reduce-only cancel can leave the position open OR already flat (see
   * `cancelReason`, e.g. POSITION_CLOSED). Consumers needing the final state
   * must read the positions/account queries.
   */
  reduceOnly: boolean;
}

/**
 * Pushed when the async paper matching engine mutates the recipient's own
 * position (opened / reduced / closed / flipped). A wake-up: the FE invalidates
 * its paper account/positions queries so the table reflects the settled state
 * immediately instead of waiting for the next poll. Silent — no toast.
 */
export interface PaperPositionUpdatedNotificationData {
  /** Paper campaign whose account/positions changed. */
  campaignName: string;
}

/**
 * The paper-trading read surfaces a mutation can invalidate. Each emitter
 * declares only what it touched, so the client re-reads exactly those.
 * `orders` is the open-orders list (the FE query key spells it `open-orders`).
 */
export const PAPER_SURFACES = ["account", "positions", "orders", "fills"] as const;
export type PaperSurface = (typeof PAPER_SURFACES)[number];

/**
 * Pushed whenever a backend mutation changes the recipient's paper state on a
 * path the FE cannot observe synchronously (liquidation, trigger fire, epoch
 * settlement, a maker leg of someone else's match, …). A wake-up: the FE
 * invalidates the named surfaces so its tables reflect the settled state
 * immediately instead of waiting for the next poll. Silent — no toast.
 */
export interface PaperAccountChangedNotificationData {
  /** Paper campaign whose state changed. */
  campaignName: string;
  /** Non-empty; only the surfaces this mutation actually touched. */
  surfaces: PaperSurface[];
}

/**
 * API-liveness heartbeat. The backend only emits it while healthy (every 5s);
 * the client infers "down" from a heartbeat gap. Rides the notifications SSE as
 * a plain `message` event so the app opens one app-shell socket.
 */
export interface HealthNotificationData {
  /** Always "ok" — an outage surfaces as missing heartbeats, not a status. */
  status: "ok";
  /** ISO timestamp of the health check. */
  timestamp: string;
}

/**
 * Global volume/trade deltas folded into the Hub stats cache in real time.
 * Hub-only (gated server-side on `app=hub`); rides the notifications SSE as a
 * plain `message` event.
 */
export interface GlobalStatsDeltaNotificationData {
  /** Decimal string — volume added since the last delta. */
  volumeDelta: string;
  /** Trade count added since the last delta. */
  tradesDelta: number;
}

/** A KOL accepted their manager's invite. Delivered to the manager; their browser-push twin goes out when they have no stream open. */
export interface ArenaKolInviteAcceptedNotificationData {
  kolHubUserId: number;
  /** The email the invite was made for, which is how the manager knows them until they pick a name. */
  email: string;
  kolNickname: string | null;
}

/**
 * Someone asked to be friends. Delivered in-app only: it lands as a marker on
 * the Friends header button, never a modal, so there is nothing to wake a
 * closed app for.
 */
export interface ArenaFriendRequestReceivedNotificationData {
  requesterHubUserId: number;
  requesterNickname: string | null;
}

// Type mapping for notification data based on notification type
export type NotificationDataMap = {
  [NotificationType.WASH_TRADE]: WashTradeNotificationData;
  [NotificationType.SOCIAL_ACCOUNT_CONNECTED]: SocialAccountConnectedNotificationData;
  [NotificationType.MAINTENANCE]: MaintenanceNotificationData;
  [NotificationType.SPINNING_WHEEL_RESULT]: SpinningWheelResultNotificationData;
  [NotificationType.SOCIAL_ACCOUNT_DEACTIVATED]: SocialAccountDeactivatedNotificationData;
  [NotificationType.PING]: unknown;
  [NotificationType.QUEST_COMPLETED]: QuestCompletedNotificationData;
  [NotificationType.AUTOMATIC_STREAK_PROGRESS]: AutomaticStreakProgressNotificationData;
  [NotificationType.FEATURE_FLAG_CHANGED]: FeatureFlagChangedNotificationData;
  [NotificationType.AIRDROP_BOT_UPDATED]: AirdropBotUpdatedNotificationData;
  [NotificationType.EXTERNAL_ORDER_DETECTED]: ExternalOrderDetectedNotificationData;
  [NotificationType.ARENA_BALANCE_UPDATED]: ArenaBalanceUpdatedNotificationData;
  [NotificationType.ARENA_CAMPAIGN_OPT_IN_COUNT_CHANGED]: ArenaCampaignOptInCountChangedNotificationData;
  [NotificationType.ARENA_TOURNAMENT_UPDATED]: ArenaTournamentUpdatedNotificationData;
  [NotificationType.ARENA_ENTRY_UPDATED]: ArenaEntryUpdatedNotificationData;
  [NotificationType.ARENA_PRIVATE_ROOM_UPDATED]: ArenaPrivateRoomUpdatedNotificationData;
  [NotificationType.ARENA_PRIVATE_COMPETITION_STARTED]: ArenaPrivateCompetitionStartedNotificationData;
  [NotificationType.ARENA_COMPETITION_FINALIZED]: ArenaCompetitionFinalizedNotificationData;
  [NotificationType.ARENA_TICKETS_GRANTED]: ArenaTicketsGrantedNotificationData;
  [NotificationType.ARENA_PLAYER_STATS_UPDATED]: ArenaPlayerStatsUpdatedNotificationData;
  [NotificationType.ARENA_QUEUE_INVITE]: ArenaQueueInviteNotificationData;
  [NotificationType.ARENA_FRIEND_REQUEST_RECEIVED]: ArenaFriendRequestReceivedNotificationData;
  [NotificationType.ARENA_FEED_EVENT]: ArenaFeedEvent;
  [NotificationType.ARENA_GLOBAL_CHAT_MESSAGE]: ArenaGlobalChatMessageDto;
  [NotificationType.ARENA_GLOBAL_CHAT_MESSAGE_BANNED]: ArenaGlobalChatMessageBannedNotificationData;
  [NotificationType.ARENA_KOL_INVITE_ACCEPTED]: ArenaKolInviteAcceptedNotificationData;
  [NotificationType.ARENA_OPENING_MOVE_FILLED]: ArenaOpeningMoveFilledNotificationData;
  [NotificationType.ARENA_OPENING_MOVE_FAILED]: ArenaOpeningMoveFailedNotificationData;
  [NotificationType.PAPER_ORDER_CANCELLED]: PaperOrderCancelledNotificationData;
  [NotificationType.PAPER_POSITION_UPDATED]: PaperPositionUpdatedNotificationData;
  [NotificationType.PAPER_ACCOUNT_CHANGED]: PaperAccountChangedNotificationData;
  [NotificationType.USER_STATS_UPDATED]: UserStatsUpdatedNotificationData;
  [NotificationType.HEALTH]: HealthNotificationData;
  [NotificationType.GLOBAL_STATS_DELTA]: GlobalStatsDeltaNotificationData;
  [NotificationType.TRANSPORT_PING]: unknown;
};

// Generic notification interface with proper type mapping
export interface CedeNotification<T extends NotificationType = NotificationType> {
  id: string;
  type: T;
  title: string;
  message: string;
  timestamp: number;
  hubUserId?: number;
  data?: NotificationDataMap[T];
}

// Specific notification type aliases for convenience
export type WashTradeNotification = CedeNotification<NotificationType.WASH_TRADE>;
export type SocialAccountConnectedNotification = CedeNotification<NotificationType.SOCIAL_ACCOUNT_CONNECTED>;
export type SocialAccountDeactivatedNotification = CedeNotification<NotificationType.SOCIAL_ACCOUNT_DEACTIVATED>;
export type MaintenanceNotification = CedeNotification<NotificationType.MAINTENANCE>;
export type SpinningWheelResultNotification = CedeNotification<NotificationType.SPINNING_WHEEL_RESULT>;
export type QuestCompletedNotification = CedeNotification<NotificationType.QUEST_COMPLETED>;
export type AutomaticStreakProgressNotification = CedeNotification<NotificationType.AUTOMATIC_STREAK_PROGRESS>;
export type FeatureFlagChangedNotification = CedeNotification<NotificationType.FEATURE_FLAG_CHANGED>;
export type AirdropBotUpdatedNotification = CedeNotification<NotificationType.AIRDROP_BOT_UPDATED>;
export type ExternalOrderDetectedNotification = CedeNotification<NotificationType.EXTERNAL_ORDER_DETECTED>;
export type ArenaBalanceUpdatedNotification = CedeNotification<NotificationType.ARENA_BALANCE_UPDATED>;
export type ArenaCampaignOptInCountChangedNotification =
  CedeNotification<NotificationType.ARENA_CAMPAIGN_OPT_IN_COUNT_CHANGED>;
export type ArenaTournamentUpdatedNotification = CedeNotification<NotificationType.ARENA_TOURNAMENT_UPDATED>;
export type ArenaEntryUpdatedNotification = CedeNotification<NotificationType.ARENA_ENTRY_UPDATED>;
export type ArenaQueueInviteNotification = CedeNotification<NotificationType.ARENA_QUEUE_INVITE>;
export type ArenaFriendRequestReceivedNotification = CedeNotification<NotificationType.ARENA_FRIEND_REQUEST_RECEIVED>;
export type ArenaFeedEventNotification = CedeNotification<NotificationType.ARENA_FEED_EVENT>;
export type ArenaGlobalChatMessageNotification = CedeNotification<NotificationType.ARENA_GLOBAL_CHAT_MESSAGE>;
export type ArenaGlobalChatMessageBannedNotification =
  CedeNotification<NotificationType.ARENA_GLOBAL_CHAT_MESSAGE_BANNED>;
export type ArenaKolInviteAcceptedNotification = CedeNotification<NotificationType.ARENA_KOL_INVITE_ACCEPTED>;
export type ArenaPrivateRoomUpdatedNotification = CedeNotification<NotificationType.ARENA_PRIVATE_ROOM_UPDATED>;
export type ArenaPrivateCompetitionStartedNotification =
  CedeNotification<NotificationType.ARENA_PRIVATE_COMPETITION_STARTED>;
export type ArenaCompetitionFinalizedNotification = CedeNotification<NotificationType.ARENA_COMPETITION_FINALIZED>;
export type ArenaTicketsGrantedNotification = CedeNotification<NotificationType.ARENA_TICKETS_GRANTED>;
export type ArenaPlayerStatsUpdatedNotification = CedeNotification<NotificationType.ARENA_PLAYER_STATS_UPDATED>;
export type ArenaOpeningMoveFilledNotification = CedeNotification<NotificationType.ARENA_OPENING_MOVE_FILLED>;
export type ArenaOpeningMoveFailedNotification = CedeNotification<NotificationType.ARENA_OPENING_MOVE_FAILED>;
export type PaperOrderCancelledNotification = CedeNotification<NotificationType.PAPER_ORDER_CANCELLED>;
export type PaperPositionUpdatedNotification = CedeNotification<NotificationType.PAPER_POSITION_UPDATED>;
export type PaperAccountChangedNotification = CedeNotification<NotificationType.PAPER_ACCOUNT_CHANGED>;
export type UserStatsUpdatedNotification = CedeNotification<NotificationType.USER_STATS_UPDATED>;
export type HealthNotification = CedeNotification<NotificationType.HEALTH>;
export type GlobalStatsDeltaNotification = CedeNotification<NotificationType.GLOBAL_STATS_DELTA>;
export type PingNotification = CedeNotification<NotificationType.PING>;

// Discriminated union type for all notifications
export type AnyNotification =
  | WashTradeNotification
  | SocialAccountConnectedNotification
  | MaintenanceNotification
  | SpinningWheelResultNotification
  | SocialAccountDeactivatedNotification
  | QuestCompletedNotification
  | AutomaticStreakProgressNotification
  | FeatureFlagChangedNotification
  | AirdropBotUpdatedNotification
  | ExternalOrderDetectedNotification
  | ArenaBalanceUpdatedNotification
  | ArenaCampaignOptInCountChangedNotification
  | ArenaTournamentUpdatedNotification
  | ArenaEntryUpdatedNotification
  | ArenaPrivateRoomUpdatedNotification
  | ArenaPrivateCompetitionStartedNotification
  | ArenaCompetitionFinalizedNotification
  | ArenaTicketsGrantedNotification
  | ArenaPlayerStatsUpdatedNotification
  | ArenaQueueInviteNotification
  | ArenaFriendRequestReceivedNotification
  | ArenaFeedEventNotification
  | ArenaGlobalChatMessageNotification
  | ArenaGlobalChatMessageBannedNotification
  | ArenaOpeningMoveFilledNotification
  | ArenaOpeningMoveFailedNotification
  | PaperOrderCancelledNotification
  | PaperPositionUpdatedNotification
  | PaperAccountChangedNotification
  | UserStatsUpdatedNotification
  | HealthNotification
  | GlobalStatsDeltaNotification
  | PingNotification;

export type TmaQuestType = typeof QUEST_TYPE.TMA_TON_CONNECTION | typeof QUEST_TYPE.TMA_FIRST_CEX_CONNECTION;

export type TmaOnboardingReward = Record<TmaQuestType, OnboardingQuestReward>;

export interface OnboardingQuestReward {
  [QUEST_REWARD_TYPE.LOTTERY_TICKET]: number;
  [QUEST_REWARD_TYPE.USDT]: number;
}
